// src/pages/CorpPages.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection, query, where, orderBy, onSnapshot,
  doc, updateDoc, serverTimestamp, deleteDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";
import { formatDistanceToNow } from "date-fns";

// ── My Challenges ──────────────────────────────────────────────────────────────────
export function MyNeeds() {
  const { currentUser } = useAuth();
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "needs"), where("corpId", "==", currentUser.uid), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setNeeds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  async function setStatus(id, status) {
    await updateDoc(doc(db, "needs", id), { status, updatedAt: serverTimestamp() });
  }

  async function deleteNeed(id) {
    if (window.confirm("Delete this challenge? This cannot be undone.")) {
      await deleteDoc(doc(db, "needs", id));
    }
  }

  return (
    <PageShell
      title="My challenges"
      action={<Link to="/post-need" className="btn btn-primary">+ Post new challenge</Link>}
    >
      {loading ? <div className="page-loading"><div className="spinner" style={{ width: 24, height: 24 }}></div></div>
        : needs.length === 0 ? (
          <div className="empty-state card" style={{ padding: "56px 24px" }}>
            <h3>No challenges posted yet</h3>
            <p>Post your first challenge and start receiving solutions from startups.</p>
            <Link to="/post-need" className="btn btn-primary" style={{ marginTop: 16 }}>Post your first challenge</Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {needs.map(need => (
              <div key={need.id} className="card" style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <StatusBadge status={need.status} />
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {need.createdAt?.toDate ? formatDistanceToNow(need.createdAt.toDate(), { addSuffix: true }) : ""}
                      </span>
                    </div>
                    <h3 className="need-card-title" style={{ marginBottom: 6 }}>{need.title}</h3>
                    <p className="need-card-body" style={{ marginBottom: 10 }}>
                      {need.description?.slice(0, 160)}…
                    </p>
                    <div className="need-card-meta">
                      <span>💰 {need.budget}</span>
                      <span>⏱ {need.timeline}</span>
                      <span style={{ fontWeight: 500, color: need.pitchCount > 0 ? "var(--green)" : "var(--text-3)" }}>
                        📄 {need.pitchCount || 0} pitches
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", flex_direction: "column", gap: 6, flexShrink: 0, flexDirection: "column" }}>
                    <select value={need.status} onChange={e => setStatus(need.id, e.target.value)}
                      style={{ fontSize: 12, padding: "5px 8px", border: "1px solid var(--border)", borderRadius: "var(--radius)", background: "var(--surface)", cursor: "pointer" }}>
                      <option value="pending_review" disabled>Pending review</option>
                      <option value="new">New</option>
                      <option value="open">Open</option>
                      <option value="closing">Closing soon</option>
                      <option value="closed">Closed</option>
                    </select>
                    <Link to={`/need/${need.id}`} className="btn btn-outline" style={{ fontSize: 12, padding: "5px 12px", textAlign: "center" }}>View</Link>
                    <Link to={`/edit-need/${need.id}`} className="btn btn-ghost" style={{ fontSize: 12, padding: "5px 12px", textAlign: "center" }}>Edit</Link>
                    <button onClick={() => deleteNeed(need.id)} className="btn btn-ghost" style={{ fontSize: 12, color: "#d32f2f", padding: "5px 12px" }}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </PageShell>
  );
}

// ── Solutions Received ──────────────────────────────────────────────────────────
export function PitchesReceived() {
  const { currentUser } = useAuth();
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "pitches"), where("corpId", "==", currentUser.uid), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setPitches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  async function setStatus(id, status) {
    await updateDoc(doc(db, "pitches", id), { status, reviewedAt: serverTimestamp() });
  }

  const filtered = filter === "all" ? pitches : pitches.filter(p => p.status === filter);

  return (
    <PageShell title="Solutions received">
      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {["all", "pending", "shortlisted", "matched", "declined"].map(s => {
          const count = s === "all" ? pitches.length : pitches.filter(p => p.status === s).length;
          return (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: "6px 14px", borderRadius: "var(--radius)", border: "none",
              background: filter === s ? "var(--green-light)" : "var(--bg)",
              color: filter === s ? "var(--green-text)" : "var(--text-2)",
              fontWeight: filter === s ? 500 : 400, fontSize: 13, cursor: "pointer"
            }}>
              {s.charAt(0).toUpperCase() + s.slice(1)} ({count})
            </button>
          );
        })}
      </div>

      {loading ? <div className="page-loading"><div className="spinner" style={{ width: 24, height: 24 }}></div></div>
        : filtered.length === 0 ? (
          <div className="empty-state card" style={{ padding: "48px 24px" }}>
            <h3>No solutions yet</h3>
            <p>{filter === "all" ? "Pitches from startups will appear here once they respond to your challenges." : `No ${filter} pitches.`}</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map(pitch => (
              <div key={pitch.id} className="card" style={{ padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <PitchStatusBadge status={pitch.status} />
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {pitch.createdAt?.toDate ? formatDistanceToNow(pitch.createdAt.toDate(), { addSuffix: true }) : ""}
                      </span>
                    </div>
                    <div className="need-card-title" style={{ marginBottom: 4 }}>{pitch.startupName}</div>
                    <div className="need-card-industry" style={{ marginBottom: 8 }}>
                      Re: {pitch.needTitle} · {pitch.startupEmail}
                    </div>
                    <p className={`need-card-body${expanded === pitch.id ? " prose-body prose-body--sm" : ""}`} style={{ marginBottom: 0 }}>
                      {expanded === pitch.id ? pitch.summary : pitch.summary?.slice(0, 160) + (pitch.summary?.length > 160 ? "…" : "")}
                    </p>
                    {pitch.summary?.length > 160 && (
                      <button onClick={() => setExpanded(expanded === pitch.id ? null : pitch.id)}
                        style={{ fontSize: 12, color: "var(--green)", background: "none", border: "none", cursor: "pointer", marginTop: 4 }}>
                        {expanded === pitch.id ? "Show less" : "Read full pitch"}
                      </button>
                    )}
                    {expanded === pitch.id && (
                      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                        {[["Approach", pitch.approach], ["Timeline", pitch.timeline], ["Budget", pitch.budget], ["Why us", pitch.whyUs]].filter(([, v]) => v).map(([label, val]) => (
                          <div key={label}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{label}</div>
                            <p className="prose-body prose-body--sm">{val}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                    <button onClick={() => setStatus(pitch.id, "shortlisted")} className="btn btn-outline" style={{ fontSize: 12, padding: "5px 12px", color: "var(--purple-text)", borderColor: "#AFA9EC" }}>⭐ Shortlist</button>
                    <button onClick={() => setStatus(pitch.id, "matched")} className="btn btn-primary" style={{ fontSize: 12, padding: "5px 12px" }}>✓ Match</button>
                    <button onClick={() => setStatus(pitch.id, "declined")} className="btn btn-ghost" style={{ fontSize: 12, color: "#d32f2f" }}>✗ Decline</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </PageShell>
  );
}

function PageShell({ title, action, children }) {
  return (
    <div className="app-shell">
      <header className="app-page-header">
        <h1 className="app-page-title">{title}</h1>
        {action && <div className="app-page-header-action">{action}</div>}
      </header>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = { new: "badge-purple", open: "badge-green", closing: "badge-amber", closed: "badge-gray" };
  return <span className={`badge ${map[status] || "badge-gray"}`}>{status}</span>;
}

function PitchStatusBadge({ status }) {
  const map = { pending: "badge-gray", shortlisted: "badge-purple", matched: "badge-green", declined: "badge-coral" };
  return <span className={`badge ${map[status] || "badge-gray"}`}>{status}</span>;
}
